import consts from "@/common/consts";
import generateCodeSample from "../data/generateCodeSample";
import * as configPersist from "../data/persistedConfig";

const MODAL_SUPPORTED = !!~document.location.search.indexOf("withmodal");

const DEFAULT_THEME = "Default";

export const types = {
	"TOGGLE_SOURCE": "TOGGLE_SOURCE",
	"SET_ALL_SOURCES_ON": "SET_ALL_SOURCES_ON",
	"SELECT_THEME": "SELECT_THEME",
	"SET_COLOR": "SET_COLOR",
	"SELECT_FONT": "SELECT_FONT",
	"SET_ADVANCED_PARAM": "SET_ADVANCED_PARAM",
	"SET_CLOUD_PRESET": "SET_CLOUD_PRESET",
	"SET_NEW_CLOUD_PRESET": "SET_NEW_CLOUD_PRESET",
	"TOGGLE_WIDGET_MODAL": "TOGGLE_WIDGET_MODAL",
};

const SOURCES = ["local", "url", "camera", "image_search", "google_drive", "facebook", "dropbox", "instagram", "shutterstock", "getty", "istock", "unsplash"];

export const sourcesNames = { getty: 'gettyimages'}

const themes = [
	{
		name: DEFAULT_THEME,
		palette: {
			window: "#FFFFFF",
			windowBorder: "#90A0B3",
			tabIcon: "#0078FF",
			menuIcons: "#5A616A",
			textDark: "#000000",
			textLight: "#FFFFFF",
			link: "#0078FF",
			action: "#FF620C",
			inactiveTabIcon: "#0E2F5A",
			error: "#F44235",
			inProgress: "#0078FF",
			complete: "#20B832",
			sourceBg: "#E4EBF1"
		},
		font: "'Fira Sans', sans-serif",
	},
	{
		name: "Minimal",
		palette: {
			"window": "#ffffff",
			"sourceBg": "#f4f4f5",
			"windowBorder": "#90a0b3",
			"tabIcon": "#000000",
			"inactiveTabIcon": "#555a5f",
			"menuIcons": "#555a5f",
			"link": "#0433ff",
			"action": "#339933",
			"inProgress": "#0433ff",
			"complete": "#339933",
			"error": "#cc0000",
			"textDark": "#000000",
			"textLight": "#fcfffd"
		},
		font: "sans-serif",
	},
	{
		name: "Office",
		palette: {
			"window": "#F5F5F5",
			"sourceBg": "#FFFFFF",
			"windowBorder": "#90a0b3",
			"tabIcon": "#0094c7",
			"inactiveTabIcon": "#69778A",
			"menuIcons": "#0094C7",
			"link": "#53ad9d",
			"action": "#8F5DA5",
			"inProgress": "#0194c7",
			"complete": "#53ad9d",
			"error": "#c43737",
			"textDark": "#000000",
			"textLight": "#FFFFFF",
		}
		,
		font: "'Poppins', sans-serif",
	},
	{
		name: "Woody",
		palette: {
			"window": "#464040",
			"sourceBg": "#292222",
			"windowBorder": "#c7a49f",
			"tabIcon": "#cc6600",
			"inactiveTabIcon": "#E8D5BB",
			"menuIcons": "#ebe5db",
			"link": "#ffb107",
			"action": "#ffcc00",
			"inProgress": "#99cccc",
			"complete": "#78b3b4",
			"error": "#ff6666",
			"textDark": "#4C2F1A",
			"textLight": "#D8CFCF",
		},
		font: "'Merriweather', serif",
	},
	{
		name: "Dark",
		palette: {
			window: "#000000",
			sourceBg: "#000000",
			windowBorder: "#8E9FBF",
			tabIcon: "#FFFFFF",
			inactiveTabIcon: "#8E9FBF",
			menuIcons: "#2AD9FF",
			link: "#08C0FF",
			action: "#336BFF",
			inProgress: "#00BFFF",
			complete: "#33ff00",
			error: "#EA2727",
			textDark: "#000000",
			textLight: "#FFFFFF"
		},
		font: "'Space Mono', monospace",
	},
	{
		name: "Purple",
		palette: {
			"window": "#5D005D",
			"sourceBg": "#3A0A3A",
			"windowBorder": "#AD5BA3",
			"tabIcon": "#ffffcc",
			"inactiveTabIcon": "#FFD1D1",
			"menuIcons": "#FFD1D1",
			"link": "#ffcc33",
			"action": "#ffcc33",
			"inProgress": "#00e6b3",
			"complete": "#a6ff6f",
			"error": "#ff1765",
			"textDark": "#3c0d68",
			"textLight": "#fcfffd",
		},
		font: "'Kalam', cursive",
	},
	{
		name: "Twilight",
		sliderColor: "#060232",
		palette: {
			"window": "#10173a",
			"sourceBg": "#20304b",
			"windowBorder": "#7171D0",
			"tabIcon": "#79F7FF",
			"inactiveTabIcon": "#8E9FBF",
			"menuIcons": "#CCE8FF",
			"link": "#72F1FF",
			"action": "#5333FF",
			"inProgress": "#00ffcc",
			"complete": "#33ff00",
			"error": "#cc3333",
			"textDark": "#000000",
			"textLight": "#ffffff"
		},
		font: "'IBM Plex Sans', sans-serif",
	},
];

const initialWidgetConfig = {
	cloudName: null,
	uploadPreset: null,
	activeSources: SOURCES.slice(),
	advanced: {
		showAdvancedOptions: true,
		cropping: true,
		multiple: false,
		defaultSource: "local",
	},
	colors: themes[0].palette,
};

export const getConfigForCodeSample = (state) => ({
	cloudName: state.cloudName || "<cloud name>",
	uploadPreset: state.uploadPreset || "<upload preset>",
	sources: state.activeSources,
	googleApiKey: "<image_search_google_api_key>",
	...state.advanced,
	styles: {
		palette: {
			...state.colors,
		},
		fonts: {
			"default": null,
			[state.font]: {
				url: consts.FONTS[state.font],
				active: true
			},
		},
	},
});

const updateCodeSample = (state) => {
	const config = getConfigForCodeSample(state);

	state.codeSample = generateCodeSample(config);
	state.codeSampleConfig = config;
};

const getTheme = (themeName) =>
	themes.find((t) => t.name === themeName);

export const getThemeColors = (themeName) => {
	const theme = getTheme(themeName);

	const colors = theme ?
		theme.palette :
		getTheme(DEFAULT_THEME).palette;

	return {...colors};
};

export const getThemeFont = (themeName) => {
	const theme = getTheme(themeName);

	return theme ?
		theme.font :
		getTheme(DEFAULT_THEME).font;
};

export const createInitialState = () => {
	const persisted = configPersist.load() || {};

	const widgetConfig = {
			...initialWidgetConfig,
			...persisted.widgetConfig,
			font: persisted.font || "default",
		},
		selectedTheme = persisted.selectedTheme || DEFAULT_THEME,
		colors = selectedTheme !== consts.CUSTOM ?
			getThemeColors(selectedTheme) : widgetConfig.colors;

	const codeSampleConfig = getConfigForCodeSample(widgetConfig);

	return {
		modalSupported: MODAL_SUPPORTED,
		modal: MODAL_SUPPORTED && persisted.modal || false,
		sources: SOURCES.slice(),
		selectedTheme,
		themes,
		colors,
		...widgetConfig,
		codeSampleConfig,
		codeSample: generateCodeSample(codeSampleConfig),
		newCloudName: null,
		newUploadPreset: null,
	};
};

/* initial state */
const state = createInitialState();

export const persistConfig = (state) => {
	const config = {
		selectedTheme: state.selectedTheme,
		modal: MODAL_SUPPORTED && state.modal,
		font: state.font,
		widgetConfig: {
			cloudName: state.cloudName,
			uploadPreset: state.uploadPreset,
			activeSources: state.activeSources,
			advanced: state.advanced,
			colors: state.colors,
		}
	};

	configPersist.save(config);
};

const getConfigChangingAction = (action) =>
	(options, ...args) => {
		if (typeof action === "function") {
			action(options, ...args);
		}
		else {
			options.commit(action, ...args);
		}

		persistConfig(options.state);
	};

const actions = {
	[types.TOGGLE_SOURCE]:
		getConfigChangingAction(types.TOGGLE_SOURCE),

	[types.SELECT_THEME]: getConfigChangingAction(types.SELECT_THEME),

	[types.SELECT_FONT]: getConfigChangingAction(types.SELECT_FONT),

	[types.SET_COLOR]: getConfigChangingAction(({commit}, payload) => {
		commit(types.SELECT_THEME, {theme: consts.CUSTOM});
		commit(types.SET_COLOR, payload)
	}),

	[types.SET_ADVANCED_PARAM]: getConfigChangingAction(types.SET_ADVANCED_PARAM),

	[types.SET_ALL_SOURCES_ON]({commit}) {
		commit(types.SET_ALL_SOURCES_ON);
	},

	[types.SET_CLOUD_PRESET]: getConfigChangingAction(types.SET_CLOUD_PRESET),

	[types.SET_NEW_CLOUD_PRESET]({commit}, payload) {
		commit(types.SET_NEW_CLOUD_PRESET, payload);
	},

	[types.TOGGLE_WIDGET_MODAL]: getConfigChangingAction(types.TOGGLE_WIDGET_MODAL),
};

/**
 * helper to make sure the code sample is updated with the config changes done by a mutation
 * @param mutation
 * @returns {Function}
 */
const getConfigChangingMutation = (mutation) =>
	(state, ...args) => {
		mutation(state, ...args);
		updateCodeSample(state);
	};

const mutations = {
	[types.TOGGLE_SOURCE]: getConfigChangingMutation((state, {src, only}) => {
		let newSources = [];

		if (only) {
			newSources.push(src);
		}
		else {
			const indx = state.activeSources.indexOf(src);

			newSources = newSources.concat(state.activeSources);

			if (~indx) {
				newSources.splice(indx, 1);
			}
			else {
				newSources.push(src);
			}
		}

		state.activeSources = newSources;
	}),

	[types.SELECT_THEME]: getConfigChangingMutation((state, {theme}) => {
		state.selectedTheme = theme;

		if (theme !== consts.CUSTOM) {
			state.colors = getThemeColors(theme);
			state.font = getThemeFont(theme);
		}
	}),

	[types.SELECT_FONT]: getConfigChangingMutation((state, {font}) => {
		state.font = font;
	}),

	[types.SET_COLOR]: getConfigChangingMutation((state, {name, value}) => {
		state.colors = {
			...state.colors,
			[name]: value,
		};
	}),

	[types.SET_ADVANCED_PARAM]: getConfigChangingMutation((state, {name, value}) => {
		state.advanced = {
			...state.advanced,
			[name]: value,
		};

		if (name === "cropping" && value && state.advanced.multiple) {
			state.advanced.multiple = false;
		}

		if (name === "multiple" && value && state.advanced.cropping) {
			state.advanced.cropping = false;
		}
	}),

	[types.SET_ALL_SOURCES_ON]: getConfigChangingMutation((state) => {
		state.activeSources = SOURCES.slice();
	}),

	[types.SET_CLOUD_PRESET]: getConfigChangingMutation((state, {cloudName, uploadPreset}) => {
		state.cloudName = cloudName;
		state.uploadPreset = uploadPreset;
	}),

	[types.SET_NEW_CLOUD_PRESET]: (state, payload) => {
		state.newCloudName = payload.cloudName;
		state.newUploadPreset = payload.uploadPreset;
	},

	[types.TOGGLE_WIDGET_MODAL]: getConfigChangingMutation((state) => {
		state.modal = MODAL_SUPPORTED && !state.modal;
	}),
};

const getters = {
	getWidgetState: (state) => ({
		modalSupported: state.modalSupported,
		modal: state.modal,
		font: state.font,
		sources: state.activeSources,
		cloudName: state.cloudName,
		uploadPreset: state.uploadPreset,
		themes: state.themes,
		colors: state.colors,
		advanced: state.advanced,
	}),
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
}
